import { omit } from 'lodash';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { AiBatchReviewRecordChange } from '@/modules/ai/models/AiBatchReviewRecordChange';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { fullMonthDayYearDateFormat } from '@/modules/core/app/helpers/DateHelper';
import { ExtractedRecordUnfilledField } from '@/modules/ai/models/ExtractedRecordUnfilledField';

export abstract class BaseExtractedRecordModel extends BaseEntityModel {
  matchSummary?: string;
  type?: string;
  unfilled: ExtractedRecordUnfilledField[];
  provenance?: Record<string, unknown>;
  isValidated?: boolean;
  sourceDocumentIds: number[];
  isSelected: boolean = false;
  private _matchedIdOnFile: number | null = null;

  constructor(model: Partial<BaseExtractedRecordModel> = {}) {
    super(model);

    this.matchSummary = model.matchSummary;
    this.type = model.type;
    this.unfilled = ExtractedRecordUnfilledField.mapModels(model.unfilled);
    this.provenance = model.provenance;
    this.isValidated = model.isValidated;
    this.sourceDocumentIds = model.sourceDocumentIds ?? [];
  }

  abstract get matchedRecord(): BaseEntityModel | null;

  protected abstract get matchedRecordKey(): string;

  // Each staging table stores its match as its own foreign key, and the import updates the
  // matched record while that key is set, so the key is the reviewer's choice.
  protected abstract get matchedId(): number | null | undefined;

  protected abstract set matchedId(id: number | null | undefined);

  abstract get label(): string;

  abstract get matchedLabel(): string;

  protected abstract get comparedValues(): (AiBatchReviewRecordChange | null)[];

  get changes(): AiBatchReviewRecordChange[] {
    return this.comparedValues.filter(
      (change: AiBatchReviewRecordChange | null): change is AiBatchReviewRecordChange =>
        change instanceof AiBatchReviewRecordChange
    );
  }

  get hasMatch(): boolean {
    return !!this.matchedRecord;
  }

  get isUpdatingMatch(): boolean {
    return !!this.matchedId;
  }

  chooseToUpdateMatch(isUpdating: boolean): void {
    this.matchedId = isUpdating ? this._matchedIdOnFile : null;
  }

  get importPayload(): Record<string, unknown> {
    return omit(this, [this.matchedRecordKey, 'isSelected']);
  }

  protected rememberMatchOnFile(): void {
    this._matchedIdOnFile = this.matchedId ?? null;
  }

  protected changeFor(
    label: string,
    matched: unknown,
    extracted: unknown,
    format: (value: unknown) => string = String,
    normalize: (value: unknown) => string = format
  ): AiBatchReviewRecordChange | null {
    if (this.isBlank(extracted)) {
      return null;
    }

    if (!this.isBlank(matched) && normalize(matched) === normalize(extracted)) {
      return null;
    }

    return new AiBatchReviewRecordChange(
      label,
      this.isBlank(matched) ? '-' : format(matched),
      format(extracted)
    );
  }

  protected formatDate(value: unknown): string {
    return fullMonthDayYearDateFormat(String(value).slice(0, 10));
  }

  protected formatAmount(value: unknown): string {
    return NumeralService.formatValue(value, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  private isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  get showAmountColumn(): boolean {
    return true;
  }

  get avatarLetters(): string {
    return '';
  }

  get relationshipLabel(): string {
    return '';
  }

  get hasMissingInfo(): boolean {
    return this.unfilled.length > 0;
  }

  get unfilledFields(): string[] {
    return this.unfilled
      .map((unfilled: ExtractedRecordUnfilledField): string | undefined => unfilled.field)
      .filter((field: string | undefined): field is string => !!field);
  }
}
